import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { PencilSimple, Eraser, PaperPlaneTilt } from '@phosphor-icons/react';
import { API } from '@/config/api';

const DECLARATIONS = [
  'I fully understand and acknowledge the total tuition fees associated with my program, including any funding through Student Aid BC.',
  'I acknowledge that the program is self-paced and I am responsible for managing my progress.',
  'I have reviewed and understood the program curriculum, modules, and completion requirements.',
  'I understand how this program enhances my professional skills and employability.',
  'I confirm my awareness of Student Aid BC obligations, including repayment policies.',
  'I am enrolling voluntarily and have had the opportunity to ask questions.',
];

/**
 * Fill Online dialog for the Student Declaration Form.
 * Auto-pre-fills from the student profile; lets the recruiter (or student) tick
 * declarations and sign via drawn canvas or typed cursive.
 * On submit, POSTs to /api/students/{id}/declaration/generate which generates
 * a 1:1 PDF and uploads it as the student_declaration_form doc.
 */
export default function FillDeclarationDialog({ open, onClose, student, collegeName, onGenerated }) {
  // Derive the ITD branch from the selected college name. The two known ITD
  // branches are "North Vancouver" and "Vernon" — recruiter can override before
  // generating the PDF in the rare case the auto-derive guesses wrong.
  const deriveBranch = (name = '') => {
    const lower = name.toLowerCase();
    if (lower.includes('vernon')) return 'Vernon';
    if (lower.includes('north vancouver') || lower.includes('north van')) return 'North Vancouver';
    return '';
  };
  const [form, setForm] = useState({
    full_name: student?.full_name || '',
    date_of_birth: student?.dob || '',
    student_id_str: '',
    email: student?.email || '',
    phone: student?.phone ? `${student.phone_code || ''} ${student.phone}`.trim() : '',
    program_name: student?.preferred_program_name || student?.preferred_program || '',
    // Always 52 weeks — ITD's standard diploma cadence. Recruiter can still
    // override if a college ever adds a shorter program.
    program_duration: '52 weeks',
    // Auto-fill the program start from the intake the recruiter picked so
    // the recruiter doesn't type it twice. Falls back to blank if no intake.
    program_start_date: (student?.intake_date || '').slice(0, 10),
    program_delivery_mode: 'self_paced',
    total_tuition_fees: student?.sale_price ? String(student.sale_price) : '',
    funding_source: 'student_aid_bc',
    funding_other: '',
    branch: deriveBranch(collegeName),
  });
  const [decls, setDecls] = useState(DECLARATIONS.map(() => false));
  const [signMode, setSignMode] = useState('draw'); // 'draw' | 'type'
  const [typedSig, setTypedSig] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  // Re-derive branch when the recruiter changes the college and reopens the dialog
  useEffect(() => {
    if (!open) return;
    setForm(prev => prev.branch ? prev : { ...prev, branch: deriveBranch(collegeName) });
  }, [open, collegeName]);

  // Setup canvas
  useEffect(() => {
    if (signMode !== 'draw' || !canvasRef.current) return;
    const c = canvasRef.current;
    c.width = c.offsetWidth * 2;
    c.height = c.offsetHeight * 2;
    const ctx = c.getContext('2d');
    ctx.scale(2, 2);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#0f172a';
  }, [signMode, open]);

  const getPoint = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const moveDraw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPoint(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endDraw = () => { drawing.current = false; };

  const clearSignature = () => {
    if (signMode === 'draw' && canvasRef.current) {
      const c = canvasRef.current;
      const ctx = c.getContext('2d');
      ctx.clearRect(0, 0, c.width, c.height);
    } else {
      setTypedSig('');
    }
  };

  const allDeclsAccepted = decls.every(Boolean);

  const hasSignature = () => {
    if (signMode === 'type') return !!typedSig.trim();
    if (!canvasRef.current) return false;
    // Cheap check: read a few pixels — if any are non-transparent, it's been drawn on
    const ctx = canvasRef.current.getContext('2d');
    const data = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height).data;
    for (let i = 3; i < data.length; i += 4) if (data[i] !== 0) return true;
    return false;
  };

  const handleSubmit = async () => {
    if (!form.full_name.trim()) { toast.error('Full name is required'); return; }
    if (!form.branch) { toast.error('Pick an ITD branch (North Vancouver or Vernon)'); return; }
    if (!allDeclsAccepted) { toast.error('Please accept all declarations'); return; }
    if (!hasSignature()) { toast.error('Please provide a signature'); return; }

    const payload = {
      ...form,
      declarations_accepted: decls,
      signature_image_b64: signMode === 'draw' ? canvasRef.current.toDataURL('image/png') : null,
      signature_typed_text: signMode === 'type' ? typedSig.trim() : null,
      signed_date: new Date().toISOString().slice(0, 10),
    };
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/students/${student.id}/declaration/generate`, payload);
      onGenerated?.(res.data.file);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate declaration');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!submitting) onClose?.(); if (!o) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="fill-declaration-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Libre Baskerville', serif" }}>Fill Student Declaration Form</DialogTitle>
          <DialogDescription>
            {collegeName ? `${collegeName} — ` : ''}fill the fields, accept all declarations, sign, and submit. A 1:1 PDF will be generated and uploaded.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Student Info */}
          <div>
            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-2">Student Information</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Full Name *</Label><Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} data-testid="decl-full-name" /></div>
              <div><Label className="text-xs">Date of Birth</Label><Input type="date" value={form.date_of_birth} onChange={e => setForm({ ...form, date_of_birth: e.target.value })} data-testid="decl-dob" /></div>
              <div><Label className="text-xs">Student ID</Label><Input value={form.student_id_str} onChange={e => setForm({ ...form, student_id_str: e.target.value })} placeholder="If applicable" data-testid="decl-sid" /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="decl-email" /></div>
              <div className="sm:col-span-2"><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="decl-phone" /></div>
            </div>
          </div>

          {/* Program */}
          <div>
            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-2">Program Details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs">ITD Branch *</Label>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="decl-branch-picker">
                  {['North Vancouver', 'Vernon'].map(b => (
                    <button
                      key={b}
                      type="button"
                      onClick={() => setForm({ ...form, branch: b })}
                      className={`text-xs px-3 py-1.5 rounded-full border transition ${form.branch === b ? 'bg-blue-700 text-white border-blue-700 shadow-sm' : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'}`}
                      data-testid={`decl-branch-${b.toLowerCase().replace(/\s+/g, '-')}`}
                    >{b}</button>
                  ))}
                </div>
                <p className="text-[10px] text-gray-500 mt-1">Auto-detected from the selected college — change if needed. Stamped at the top of the generated PDF.</p>
              </div>
              <div><Label className="text-xs">Program Name</Label><Input value={form.program_name} onChange={e => setForm({ ...form, program_name: e.target.value })} data-testid="decl-prog" /></div>
              <div><Label className="text-xs">Program Duration</Label><Input value={form.program_duration} onChange={e => setForm({ ...form, program_duration: e.target.value })} placeholder="e.g. 6 months" data-testid="decl-duration" /></div>
              <div><Label className="text-xs">Start Date</Label><Input type="date" value={form.program_start_date} onChange={e => setForm({ ...form, program_start_date: e.target.value })} data-testid="decl-start" /></div>
              <div>
                <Label className="text-xs">Delivery Mode</Label>
                <div className="flex gap-3 mt-2">
                  <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                    <input type="radio" name="mode" value="self_paced" checked={form.program_delivery_mode === 'self_paced'} onChange={() => setForm({ ...form, program_delivery_mode: 'self_paced' })} /> Self-Paced
                  </label>
                  <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                    <input type="radio" name="mode" value="online" checked={form.program_delivery_mode === 'online'} onChange={() => setForm({ ...form, program_delivery_mode: 'online' })} /> Online
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Tuition */}
          <div>
            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-2">Tuition Fees</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Total Tuition (CAD)</Label><Input value={form.total_tuition_fees} onChange={e => setForm({ ...form, total_tuition_fees: e.target.value })} placeholder="e.g. 12,500" data-testid="decl-tuition" /></div>
              <div>
                <Label className="text-xs">Funding Source</Label>
                <select value={form.funding_source} onChange={e => setForm({ ...form, funding_source: e.target.value })} className="w-full h-9 border rounded-md px-2 text-sm" data-testid="decl-funding">
                  <option value="student_aid_bc">Student Aid BC Loan</option>
                  <option value="self_funded">Self-Funded</option>
                  <option value="other">Other</option>
                </select>
              </div>
              {form.funding_source === 'other' && (
                <div className="sm:col-span-2"><Label className="text-xs">Other (specify)</Label><Input value={form.funding_other} onChange={e => setForm({ ...form, funding_other: e.target.value })} data-testid="decl-funding-other" /></div>
              )}
            </div>
          </div>

          {/* Declarations */}
          <div>
            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-2">Declarations (accept all)</p>
            <div className="space-y-2">
              {DECLARATIONS.map((d, i) => (
                <label key={i} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`decl-check-${i}`}>
                  <Checkbox checked={decls[i]} onCheckedChange={() => setDecls(prev => prev.map((v, idx) => idx === i ? !v : v))} />
                  <span className="text-gray-700">{d}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Signature */}
          <div>
            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-2">Signature of Student *</p>
            <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5 mb-2">
              <button type="button" onClick={() => setSignMode('draw')} className={`px-3 py-1 text-xs ${signMode === 'draw' ? 'bg-white shadow-sm rounded' : 'text-gray-500'}`}>Draw</button>
              <button type="button" onClick={() => setSignMode('type')} className={`px-3 py-1 text-xs ${signMode === 'type' ? 'bg-white shadow-sm rounded' : 'text-gray-500'}`}>Type</button>
            </div>
            {signMode === 'draw' ? (
              <div className="border-2 border-dashed border-gray-300 rounded-md bg-white">
                <canvas
                  ref={canvasRef}
                  className="w-full h-32 touch-none"
                  onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={endDraw} onMouseLeave={endDraw}
                  onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={endDraw}
                  data-testid="signature-canvas"
                />
              </div>
            ) : (
              <Input
                value={typedSig}
                onChange={e => setTypedSig(e.target.value)}
                placeholder="Type your full name"
                className="h-12 text-2xl italic font-bold text-blue-900"
                style={{ fontFamily: "'Brush Script MT', cursive" }}
                data-testid="signature-typed"
              />
            )}
            <button type="button" onClick={clearSignature} className="text-xs text-gray-500 hover:text-gray-700 mt-1.5 inline-flex items-center gap-1"><Eraser size={12} /> Clear</button>
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
            <Button variant="ghost" onClick={onClose} disabled={submitting} data-testid="decl-cancel">Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || !allDeclsAccepted} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="decl-submit">
              <PaperPlaneTilt size={14} className="mr-1.5" />
              {submitting ? 'Generating PDF…' : 'Generate & Upload PDF'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
